import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Loader2, MapPin, Navigation, CalendarClock, Users, ArrowLeft, CheckCircle2, Circle } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { fetchReservation } from "@/lib/api";

const STEPS = [
  { key: "confirmee", label: "Demande reçue", desc: "Votre réservation est confirmée et enregistrée." },
  { key: "assigne", label: "Chauffeur assigné", desc: "Un chauffeur prépare votre prise en charge." },
  { key: "en_route", label: "En route", desc: "Votre chauffeur arrive au point de départ." },
  { key: "terminee", label: "Course terminée", desc: "Merci de votre confiance. À très vite." },
];

export default function Tracking() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const search = async (value) => {
    const c = (value ?? code).trim();
    if (!c) return;
    setLoading(true);
    setError(null);
    try {
      setData(await fetchReservation(c));
    } catch (err) {
      setData(null);
      setError(err.response?.data?.detail || "Aucune réservation trouvée.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const c = params.get("code");
    if (c) { setCode(c); search(c); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeIndex = data ? STEPS.findIndex((s) => s.key === data.status) : -1;

  return (
    <main className="min-h-screen flex flex-col" data-testid="tracking-page">
      <Nav />
      <section className="flex-1 pt-40 pb-24 px-6 sm:px-12 lg:px-20 max-w-5xl mx-auto w-full">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.215, 0.61, 0.355, 1] }}>
          <Link to="/" data-testid="tracking-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-gold transition-colors mb-10">
            <ArrowLeft size={13} /> Retour au site
          </Link>
          <p className="text-xs uppercase tracking-[0.25em] font-mono text-amber-700/80 font-semibold mb-6">Suivi client</p>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] mb-12">
            Où en est <span className="italic text-gold">ma course ?</span>
          </h1>

          <div className="flex flex-col sm:flex-row gap-4 mb-14">
            <input
              data-testid="tracking-code-input"
              className="flex-1 bg-white border border-[#E6E1D8] px-5 py-4 font-mono text-sm tracking-[0.15em] uppercase placeholder:normal-case placeholder:tracking-normal placeholder:text-neutral-400 focus:outline-none focus:border-gold transition-colors"
              placeholder="Votre code — ex : VTC-4821"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && search()}
            />
            <button
              data-testid="tracking-search-button"
              onClick={() => search()}
              disabled={loading}
              className="bg-obsidian text-cream text-xs uppercase tracking-[0.2em] px-8 py-4 hover:bg-gold hover:text-obsidian transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Rechercher
            </button>
          </div>

          <AnimatePresence mode="wait">
            {error && (
              <motion.p key="err" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-sm text-red-700 border border-red-200 bg-red-50 px-5 py-4" data-testid="tracking-error">
                {error}
              </motion.p>
            )}
            {data && (
              <motion.div key="result" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.7, ease: [0.215, 0.61, 0.355, 1] }} className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-7 bg-white border border-[#E6E1D8] p-8 lg:p-10">
                  <div className="flex items-center justify-between mb-10">
                    <span className="font-mono text-sm tracking-[0.15em] text-neutral-500" data-testid="tracking-code-display">{data.code}</span>
                    <span className="text-xs uppercase tracking-[0.2em] bg-gold/10 text-amber-700 border border-gold/40 px-4 py-2" data-testid="tracking-status-badge">
                      {STEPS[activeIndex]?.label}
                    </span>
                  </div>
                  <ol className="space-y-0">
                    {STEPS.map((s, i) => {
                      const done = i < activeIndex;
                      const active = i === activeIndex;
                      return (
                        <li key={s.key} className="relative flex gap-5 pb-8 last:pb-0" data-testid={`tracking-step-${s.key}`}>
                          {i < STEPS.length - 1 && <span className={`absolute left-[13px] top-7 bottom-0 w-px ${done || active ? "bg-gold" : "bg-[#E6E1D8]"}`} />}
                          {done || active ? <CheckCircle2 size={27} className={`shrink-0 ${active ? "text-gold" : "text-gold/60"}`} /> : <Circle size={27} className="shrink-0 text-[#E6E1D8]" />}
                          <div>
                            <p className={`text-sm font-medium ${active ? "text-obsidian" : done ? "text-neutral-600" : "text-neutral-400"}`}>{s.label}</p>
                            <p className="text-xs text-neutral-500 mt-1">{s.desc}</p>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </div>
                <aside className="lg:col-span-5 bg-obsidian text-cream p-8 lg:p-10" data-testid="tracking-details">
                  <p className="text-xs uppercase tracking-[0.25em] font-mono text-gold mb-8">Détails de la course</p>
                  <dl className="space-y-5 text-sm">
                    <div className="flex gap-3"><MapPin size={15} className="text-gold shrink-0 mt-0.5" /><div><dt className="text-neutral-400 text-xs mb-1">Départ</dt><dd>{data.pickup}</dd></div></div>
                    {data.dropoff && <div className="flex gap-3"><Navigation size={15} className="text-gold shrink-0 mt-0.5" /><div><dt className="text-neutral-400 text-xs mb-1">Arrivée</dt><dd>{data.dropoff}</dd></div></div>}
                    <div className="flex gap-3"><CalendarClock size={15} className="text-gold shrink-0 mt-0.5" /><div><dt className="text-neutral-400 text-xs mb-1">Prise en charge</dt><dd>{new Date(data.pickup_datetime).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}</dd></div></div>
                    <div className="flex gap-3"><Users size={15} className="text-gold shrink-0 mt-0.5" /><div><dt className="text-neutral-400 text-xs mb-1">Prestation</dt><dd data-testid="tracking-trip-type">{data.trip_type === "disposition" ? `Mise à disposition · ${data.hours} h` : `Course ${data.category === "long" ? "longue distance" : "courte distance"}`} · {data.vehicle_label} · {data.passengers} passager{data.passengers > 1 ? "s" : ""}</dd></div></div>
                    <div className="border-t border-neutral-800 pt-5 flex justify-between items-baseline">
                      <dt className="text-neutral-400 text-xs">{data.trip_type === "disposition" ? `${data.hours} h de mise à disposition` : `${data.distance_km} km · ~${data.duration_min} min`}</dt>
                      <dd className="font-serif text-3xl text-gold" data-testid="tracking-price">{data.price.toFixed(2)} €</dd>
                    </div>
                  </dl>
                </aside>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </section>
      <Footer />
    </main>
  );
}
